import React, { useRef } from 'react';
import Editor from '@monaco-editor/react';
import { FaCode } from 'react-icons/fa';

const SQLInputWithSuggestions = ({ onQueryChange }) => {
  const editorRef = useRef(null);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // Add custom SQL keywords and functions
    monaco.languages.registerCompletionItemProvider('sql', {
      triggerCharacters: ['.', ' ', '(', '[', '{', '=', '>', '<', '!', '~', '+', '-', '*', '/', '%', '&', '|', '^', '?', ':', ';', ',', '"', "'"],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const suggestions = [
          // SQL Keywords
          {
            label: 'SELECT',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'SELECT',
            detail: 'SQL Keyword',
            sortText: '0SELECT',
            range: range
          },
          {
            label: 'FROM',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'FROM',
            detail: 'SQL Keyword',
            sortText: '0FROM',
            range: range
          },
          {
            label: 'WHERE',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'WHERE',
            detail: 'SQL Keyword',
            sortText: '0WHERE',
            range: range
          },
          {
            label: 'AND',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'AND',
            detail: 'SQL Keyword',
            sortText: '0AND',
            range: range
          },
          {
            label: 'OR',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'OR',
            detail: 'SQL Keyword',
            sortText: '0OR',
            range: range
          },
          {
            label: 'LIKE',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'LIKE',
            detail: 'SQL Keyword',
            sortText: '0LIKE',
            range: range
          },
          {
            label: 'IN',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'IN',
            detail: 'SQL Keyword',
            sortText: '0IN',
            range: range
          },
          {
            label: 'BETWEEN',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'BETWEEN',
            detail: 'SQL Keyword',
            sortText: '0BETWEEN',
            range: range
          },
          {
            label: 'IS NULL',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'IS NULL',
            detail: 'SQL Keyword',
            sortText: '0IS NULL',
            range: range
          },
          {
            label: 'IS NOT NULL',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'IS NOT NULL',
            detail: 'SQL Keyword',
            sortText: '0IS NOT NULL',
            range: range
          },
          {
            label: 'ORDER BY',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'ORDER BY',
            detail: 'SQL Keyword',
            sortText: '0ORDER BY',
            range: range
          },
          {
            label: 'GROUP BY',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'GROUP BY',
            detail: 'SQL Keyword',
            sortText: '0GROUP BY',
            range: range
          },
          {
            label: 'LIMIT',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'LIMIT',
            detail: 'SQL Keyword',
            sortText: '0LIMIT',
            range: range
          },
          // Field suggestions
          {
            label: 'id',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'id',
            detail: 'Field',
            sortText: '1id',
            range: range
          },
          {
            label: 'name',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'name',
            detail: 'Field',
            sortText: '1name',
            range: range
          },
          {
            label: 'age',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'age',
            detail: 'Field',
            sortText: '1age',
            range: range
          },
          {
            label: 'job_link',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'job_link',
            detail: 'Field',
            sortText: '1job_link',
            range: range
          },
          {
            label: 'role_name',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'role_name',
            detail: 'Field',
            sortText: '1role_name',
            range: range
          },
          {
            label: 'company_name',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'company_name',
            detail: 'Field',
            sortText: '1company_name',
            range: range
          },
          {
            label: 'job_location',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'job_location',
            detail: 'Field',
            sortText: '1job_location',
            range: range
          },
          {
            label: 'job_post_at',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'job_post_at',
            detail: 'Field',
            sortText: '1job_post_at',
            range: range
          },
          {
            label: 'description',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'description',
            detail: 'Field',
            sortText: '1description',
            range: range
          },
          {
            label: 'skills',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'skills',
            detail: 'Field',
            sortText: '1skills',
            range: range
          },
          {
            label: 'experience',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'experience',
            detail: 'Field',
            sortText: '1experience',
            range: range
          },
          {
            label: 'salary',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'salary',
            detail: 'Field',
            sortText: '1salary',
            range: range
          },
          {
            label: 'employment_type',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'employment_type',
            detail: 'Field',
            sortText: '1employment_type',
            range: range
          },
          {
            label: 'seniority',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'seniority',
            detail: 'Field',
            sortText: '1seniority',
            range: range
          },
          {
            label: 'jobFunction',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'jobFunction',
            detail: 'Field',
            sortText: '1jobFunction',
            range: range
          },
          {
            label: 'industries',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'industries',
            detail: 'Field',
            sortText: '1industries',
            range: range
          },
          {
            label: 'vacancy',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'vacancy',
            detail: 'Field',
            sortText: '1vacancy',
            range: range
          },
          {
            label: 'relevant',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'relevant',
            detail: 'Field',
            sortText: '1relevant',
            range: range
          },
          {
            label: 'feedback',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'feedback',
            detail: 'Field',
            sortText: '1feedback',
            range: range
          },
          {
            label: 'created_at',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'created_at',
            detail: 'Field',
            sortText: '1created_at',
            range: range
          },
          {
            label: 'updated_at',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'updated_at',
            detail: 'Field',
            sortText: '1updated_at',
            range: range
          },
          {
            label: 'scraping_input_id',
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: 'scraping_input_id',
            detail: 'Field',
            sortText: '1scraping_input_id',
            range: range
          }
        ];

        return {
          suggestions: suggestions
        };
      }
    });
  };

  const formatQuery = () => {
    if (editorRef.current) {
      const query = editorRef.current.getValue();
      if (!query) return;

      try {
        // Format the WHERE clause for display
        const displayQuery = query
          // Add spaces around operators but preserve % in LIKE conditions
          .replace(/([=<>!~+\-*/%&|^?:;,])/g, (match, p1) => {
            // Don't add spaces around % in LIKE conditions
            if (p1 === '%') return p1;
            return ` ${p1} `;
          })
          // Remove multiple spaces
          .replace(/\s+/g, ' ')
          .trim()
          // Convert AND/OR to uppercase
          .replace(/\b(AND|OR)\b/gi, (match) => match.toUpperCase())
          // Add newlines after AND/OR with proper indentation
          .replace(/\b(AND|OR)\b/g, '\n  $1')
          // Split into lines and add indentation
          .split('\n')
          .map((line, index) => {
            if (index === 0) return line.trim();
            return '  ' + line.trim();
          })
          .join('\n')
          .trim();

        // Create clean version for API (no newlines, single spaces)
        const cleanQuery = query
          // Remove all newlines and extra spaces
          .replace(/\s+/g, ' ')
          .trim()
          // Convert AND/OR to uppercase
          .replace(/\b(AND|OR)\b/gi, (match) => match.toUpperCase());

        // Update the editor with formatted query for display
        editorRef.current.setValue(displayQuery);
        // Send clean version to API
        onQueryChange(cleanQuery);
      } catch (error) {
        console.error('Error formatting SQL:', error);
        // If formatting fails, keep the original query
      }
    }
  };

  return (
    <div className="w-full max-w-3xl relative">
      <div className="relative">
        <Editor
          height="150px"
          defaultLanguage="sql"
          defaultValue=""
          theme="vs-light"
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: "off",
            wordWrap: "on",
            suggestOnTriggerCharacters: true,
            quickSuggestions: {
              other: true,
              comments: true,
              strings: true
            },
            wordBasedSuggestions: true,
            snippetSuggestions: 'inline',
            contextmenu: true,
            automaticLayout: true,
            scrollBeyondLastLine: false,
            folding: false,
            lineDecorationsWidth: 0,
            lineNumbersMinChars: 0,
            glyphMargin: false,
            padding: { top: 8, bottom: 8 },
            suggest: {
              preview: true,
              showMethods: true,
              showFunctions: true,
              showConstructors: true,
              showFields: true,
              showVariables: true,
              showClasses: true,
              showStructs: true,
              showInterfaces: true,
              showModules: true,
              showProperties: true,
              showEvents: true,
              showOperators: true,
              showUnits: true,
              showValues: true,
              showConstants: true,
              showEnums: true,
              showEnumMembers: true,
              showKeywords: true,
              showWords: true,
              showColors: true,
              showFiles: true,
              showReferences: true,
              showFolders: true,
              showTypeParameters: true,
              showSnippets: true,
              filterGraceful: true,
              localityBonus: true,
              snippetsPreventQuickSuggestions: false,
              selectionMode: 'whenTriggerCharacter',
              showIcons: true,
              maxVisibleSuggestions: 12,
              filter: 'prefix'
            }
          }}
          onChange={(value) => onQueryChange(value)}
          onMount={handleEditorDidMount}
          className="border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          onClick={formatQuery}
          className="absolute right-6 top-2 p-2 bg-white rounded-md shadow-sm border border-gray-200 text-gray-500 hover:text-blue-500 hover:border-blue-500 transition-colors duration-200"
          title="Format SQL Query"
        >
          <FaCode className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default SQLInputWithSuggestions;
